import { ManualEditDeathDataModal } from '@/components/features/parcels/listing/ManualEditDeathDataModal'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { DeathData, DeathState, formatDeathState } from '@/domain/deathData/deathData'
import { ServiceType } from '@/domain/offerings/offering'
import { calculateAgeStr } from '@/utils/dates'
import { Trash2 } from 'lucide-react'
import { useEffect, useState } from 'react'

interface DeathDataCollectionProps {
  offering: { id: string; code: string; name: string; description?: string; basePrice?: number; serviceType?: ServiceType }
  onSubmit: (data: DeathData[]) => void
  onBack: () => void
  maxPeople?: number
  peopleList?: DeathData[]
  onPeopleListChange?: (data: DeathData[]) => void
}

export const DeathDataCollection = ({
  offering,
  onSubmit,
  onBack,
  maxPeople = 4,
  peopleList: externalPeopleList
}: DeathDataCollectionProps) => {
  const serviceType = offering.serviceType || ServiceType.PARCELAS
  const isCremation = serviceType === ServiceType.CREMACIONES

  // Initialize state with external data if provided, otherwise empty array
  const [peopleList, setPeopleList] = useState<DeathData[]>(externalPeopleList ?? [])
  const [error, setError] = useState<string>('')

  // Sync external changes when navigating back from previous steps
  useEffect(() => {
    if (externalPeopleList !== undefined) {
      setPeopleList(externalPeopleList)
    }
  }, [externalPeopleList])

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingRecord, setEditingRecord] = useState<DeathData | null>(null)

  const handleAddPerson = () => {
    setEditingRecord(null)
    setIsModalOpen(true)
  }

  const handleEditPerson = (index: number) => {
    setEditingRecord(peopleList[index])
    setIsModalOpen(true)
  }

  const handleModalSave = (data: DeathData) => {
    if (editingRecord) {
      // Update existing person
      setPeopleList(prev => prev.map((person, index) =>
        person.id === editingRecord.id ? { ...data, state: data.state || DeathState.DEATH } : person
      ))
    } else {
      // Add new person
      const newPerson: DeathData = {
        ...data,
        state: data.state || (isCremation ? DeathState.CREMATED : DeathState.DEATH)
      }
      setPeopleList(prev => [...prev, newPerson])
    }
    setIsModalOpen(false)
    setEditingRecord(null)
  }

  const handleModalClose = () => {
    setIsModalOpen(false)
    setEditingRecord(null)
  }

  const removePerson = (index: number) => {
    setPeopleList(prev => prev.filter((_, i) => i !== index))
    setError('')
  }

  const handleDeathDataFormSubmit = () => {
    if (peopleList.length === 0) {
      setError('Por favor agregue al menos una persona')
      return
    }
    onSubmit(peopleList)
  }

  const canAddMorePeople = peopleList.length < maxPeople

  return (
    <div className="space-y-6 min-h-[30vh] flex flex-col justify-between">
      {/* Header */}
      <div className="space-y-1">
        <h2 className="text-xl font-semibold">{"Datos de los fallecidos"}</h2>
      </div>

      {/* People List */}
      <Card>
        <CardHeader>
          {/* <CardTitle className="flex items-center justify-between"> */}
          <h4 className='text-md font-semibold'>Personas registradas ({peopleList.length}/{maxPeople})</h4>
          {/* </CardTitle> */}
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {peopleList.map((person, index) => (
              <div key={person.id} className="bg-gray-50 p-4 rounded-lg border flex justify-between items-start">
                <div className="space-y-1">
                  <p className="font-medium">{person.name}</p>
                  <p className="text-sm text-gray-600">
                    {person.docType} {person.docNumber}
                  </p>
                  <p className="text-sm text-gray-600">
                    {person.birthDate && person.deathDate
                      ? `Nacimiento: ${new Date(person.birthDate).toLocaleDateString('es-AR')}. Defunción: ${new Date(person.deathDate).toLocaleDateString('es-AR')} (${calculateAgeStr(new Date(person.birthDate), new Date(person.deathDate))})`
                      : person.deathDate
                        ? `Defunción: ${new Date(person.deathDate).toLocaleDateString('es-AR')}`
                        : person.birthDate ? `Nacimiento: ${new Date(person.birthDate!).toLocaleDateString('es-AR')}`
                          : 'Sin fechas de nacimiento y/o defunción'
                    }
                  </p>
                  {person.state && person.state !== DeathState.DEATH && (
                    <p className="text-sm text-gray-500">
                      Estado: {formatDeathState(person.state)}
                    </p>
                  )}
                  {person.relationshipWithDeceased && (
                    <p className="text-sm text-gray-500 italic">
                      Parentesco: {person.relationshipWithDeceased}
                    </p>
                  )}
                  {person.thumbLevel && (
                    <p className="text-sm text-gray-500">
                      Nivel: {person.thumbLevel}
                    </p>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => handleEditPerson(index)}
                    className="text-blue-600 hover:text-blue-800"
                  >
                    Editar
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => removePerson(index)}
                    className="text-red-600 hover:text-red-800"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}

            {/* Add Person Button */}
            {canAddMorePeople && (
              <div className="flex justify-end">
                <Button type="button" variant="secondary" onClick={handleAddPerson}>
                  Agregar Persona ({peopleList.length}/{maxPeople})
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>


      {/* Edit/Delete Modal */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      <div className="flex gap-3">
        <Button
          variant="outline"
          onClick={onBack}
          className="flex-1"
        >
          Volver
        </Button>
        <Button
          variant="info"
          onClick={handleDeathDataFormSubmit}
          className="flex-1"
        >
          Siguiente
        </Button>
      </div>

      {/* Death Data Modal */}
      <ManualEditDeathDataModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        record={editingRecord}
        onSave={handleModalSave}
        serviceType={serviceType}
      />
    </div>
  )
}